import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const getApiUrl = () => `/api`;

const PROVINCES = ['SAN_JOSE', 'ALAJUELA', 'HEREDIA', 'CARTAGO', 'GUANACASTE', 'PUNTARENAS', 'LIMON'];

const SUBSCRIPTION_TIERS = [
  {
    id: 'FREE',
    name: 'Gratuito',
    price: 0,
    period: 'mes',
    color: 'gray',
    features: [
      '5 productos publicados',
      'Soporte básico',
      'Más de 1000 manuales gratis'
    ]
  },
  {
    id: 'PRO',
    name: 'Pro',
    price: 5000,
    period: 'mes',
    color: 'blue',
    features: [
      '20 productos publicados',
      'Mayor visibilidad',
      'Vendedor verificado',
      '2 productos destacados'
    ],
    popular: false
  },
  {
    id: 'PREMIUM',
    name: 'Premium',
    price: 15000,
    period: 'mes',
    color: 'orange',
    features: [
      'Productos ilimitados',
      'Máxima visibilidad',
      'Vendedor verificado',
      'Badge de vendedor destacado',
      'Productos destacados ilimitados'
    ],
    popular: true
  }
];

export default function Register() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    province: 'SAN_JOSE',
    subscription_plan: 'FREE',
    identificacion: ''
  });
  const [asSeller, setAsSeller] = useState(false);
  const [shopType, setShopType] = useState('');
  const [showTiers, setShowTiers] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loadingName, setLoadingName] = useState(false);
  const navigate = useNavigate();

  // Fetch name from Hacienda API when identificacion changes
  const handleIdentificacionBlur = async (e) => {
    const identificacion = e.target.value.trim();
    if (!identificacion || identificacion.length < 9) return;
    
    setLoadingName(true);
    try {
      const res = await fetch(`https://api.hacienda.go.cr/fe/ae?identificacion=${identificacion}`);
      const data = await res.json();
      
      if (data && data.nombre) {
        setForm(prev => ({ ...prev, name: data.nombre }));
      }
    } catch (err) {
      console.error('Error fetching from Hacienda API:', err);
    } finally {
      setLoadingName(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Name validation: at least 2 words, letters only
    const nameParts = form.name.trim().split(/\s+/);
    if (nameParts.length < 2 || !/^[a-záéíóúüñA-ZÁÉÍÓÚÜÑ\s]+$/.test(form.name.trim())) {
      setError('Ingresa tu nombre completo (nombre y apellido, solo letras)');
      return;
    }
    
    // Email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError('Ingresa un correo electrónico válido');
      return;
    }
    
    if (form.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    setLoading(true);
    setError('');
    
    const payload = { ...form, as_seller: asSeller, shop_type: shopType || null };
    
    try {
      const res = await fetch(`${getApiUrl()}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      
      if (!res.ok) {
        setError(data.error || 'Error al registrar');
        return;
      }
      
      setSuccess(true);
      // Store message for display
      setSuccessMessage(data.message);
    } catch (err) {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    const isFree = form.subscription_plan === 'FREE';
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md text-center">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-6xl mb-4">{isFree ? '🎉' : '⏳'}</div>
            <h1 className="text-2xl font-bold mb-4">¡Registro Exitoso!</h1>
            {isFree ? (
              <>
                <p className="text-gray-600 mb-4">
                  ¡Tu cuenta está <strong className="text-green-600">activa</strong>!
                </p>
                <p className="text-gray-500 text-sm mb-6">
                  Ya puedes iniciar sesión y comenzar a usar MotoMarket CR.
                </p>
              </>
            ) : (
              <>
                <p className="text-gray-600 mb-4">
                  ¡Tu cuenta está <strong className="text-green-600">activa</strong> con plan <strong>FREE</strong>!
                </p>
                <p className="text-gray-600 mb-4">
                  El upgrade a <strong>{form.subscription_plan}</strong> está pendiente de aprobación.
                </p>
                <p className="text-gray-500 text-sm mb-6">
                  Te notificaremos cuando tu cuenta sea aprobada. Generalmente toma menos de 24 horas.
                </p>
              </>
            )}
            <Link to="/login" className="btn-primary inline-block">
              Ir a Iniciar Sesión
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <span className="text-5xl">🔧</span>
          <h1 className="text-3xl font-black mt-4">
            <span className="text-primary">MotoMarketCR</span>
          </h1>
          <p className="text-gray-500 mt-2">Crea tu cuenta para comenzar</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8">
          {error && (
            <div className="bg-red-50 text-red-600 p-4 rounded-xl mb-6 text-sm">
              {error}
            </div>
          )}

          {/* Hidden select for form submission (accessibility) */}
          <input type="hidden" name="subscription_plan" value={form.subscription_plan} />

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Cédula / Identificación *</label>
              <input
                type="text"
                value={form.identificacion}
                onChange={(e) => setForm({ ...form, identificacion: e.target.value })}
                onBlur={handleIdentificacionBlur}
                className="w-full border-2 rounded-xl px-4 py-3 focus:border-primary focus:outline-none transition"
                placeholder="Ej: 123456789"
                required
              />
              {loadingName && <p className="text-xs text-gray-500 mt-1">Buscando nombre...</p>}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Nombre completo *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full border-2 rounded-xl px-4 py-3 focus:border-primary focus:outline-none transition"
                placeholder="Tu nombre"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Correo electrónico *</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full border-2 rounded-xl px-4 py-3 focus:border-primary focus:outline-none transition"
                placeholder="tu@email.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Teléfono</label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => {
                  // Solo permitir números, espacios, guiones y signos +
                  const clean = e.target.value.replace(/[^0-9\s\-+]/g, '');
                  setForm({ ...form, phone: clean });
                }}
                className="w-full border-2 rounded-xl px-4 py-3 focus:border-primary focus:outline-none transition"
                placeholder="Ej: 8888-8888 o +506 8888 8888"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Contraseña *</label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full border-2 rounded-xl px-4 py-3 focus:border-primary focus:outline-none transition"
                placeholder="Mínimo 6 caracteres"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Provincia</label>
              <select
                value={form.province}
                onChange={(e) => setForm({ ...form, province: e.target.value })}
                className="w-full border-2 rounded-xl px-4 py-3 focus:border-primary focus:outline-none transition"
              >
                {PROVINCES.map(p => (
                  <option key={p} value={p}>{p.replace('_', ' ')}</option>
                ))}
              </select>
            </div>

            {/* Tipo de negocio para vendedores */}
            <div className={`p-4 rounded-xl border-2 transition ${asSeller ? 'border-primary bg-primary/5' : 'border-gray-200'}`}>
              <label className="block text-sm font-bold mb-3">🏪 ¿Qué tipo de negocio tienes?</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { id: '', label: 'Solo comprar', icon: '🛒', desc: 'No tengo negocio' },
                  { id: 'TALLER', label: 'Taller', icon: '🔧', desc: 'Reparación de motos' },
                  { id: 'REPUESTERA', label: 'Repuestera', icon: '🛞', desc: 'Venta de repuestos' },
                  { id: 'GRUA', label: 'Grúa', icon: '🚛', desc: 'Servicio de grúa' },
                ].map(t => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => { setAsSeller(t.id !== ''); setShopType(t.id); }}
                    className={`p-3 rounded-xl border-2 text-center transition ${
                      shopType === t.id 
                        ? 'border-primary bg-primary/10 ring-2 ring-primary' 
                        : 'border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    <span className="text-2xl">{t.icon}</span>
                    <p className="font-semibold text-sm mt-1">{t.label}</p>
                    <p className="text-xs text-gray-500">{t.desc}</p>
                  </button>
                ))}
              </div>
              {asSeller && (
                <p className="text-sm text-primary mt-2 font-medium">✓ Tu negocio aparecerá en el mapa de servicios</p>
              )}
            </div>
          </div>

          {/* Subscription Tiers - Before Submit */}
          <div className="mt-6 border-2 border-primary/20 rounded-xl p-4 bg-gradient-to-r from-primary/5 to-transparent">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg flex items-center gap-2">
                💰 Elige tu Plan de Suscripción
              </h3>
              <button
                type="button"
                onClick={() => setShowTiers(!showTiers)}
                className="text-sm text-primary hover:underline"
              >
                {showTiers ? 'Ocultar detalles' : 'Ver detalles'}
              </button>
            </div>
            
            {/* Tier Cards */}
            <div className="grid md:grid-cols-3 gap-3 mb-4">
              {SUBSCRIPTION_TIERS.map(tier => {
                const isSelected = form.subscription_plan === tier.id;
                const colorClasses = {
                  gray: isSelected ? 'border-gray-500 bg-gray-50' : 'border-gray-200 hover:border-gray-400',
                  blue: isSelected ? 'border-blue-500 bg-blue-50' : 'border-blue-200 hover:border-blue-400',
                  orange: isSelected ? 'border-orange-500 bg-orange-50' : 'border-orange-200 hover:border-orange-400'
                };
                const badgeClasses = {
                  gray: 'bg-gray-500',
                  blue: 'bg-blue-500',
                  orange: 'bg-gradient-to-r from-yellow-500 to-orange-500'
                };
                
                return (
                  <div
                    key={tier.id}
                    onClick={() => setForm({ ...form, subscription_plan: tier.id })}
                    className={`cursor-pointer border-2 rounded-xl p-4 transition-all ${colorClasses[tier.color]} ${isSelected ? 'ring-2 ring-primary' : ''}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className={`${tier.id === 'FREE' ? 'font-semibold' : 'font-bold text-lg'}`}>
                        {tier.name}
                      </span>
                      {isSelected && (
                        <span className="text-primary text-lg">✓</span>
                      )}
                    </div>
                    <p className={`text-xl font-bold ${tier.id === 'FREE' ? 'text-gray-600' : 'text-primary'}`}>
                      {tier.price === 0 ? 'Gratis' : `₡${tier.price.toLocaleString('es-CR')}`}
                      {tier.price > 0 && <span className="text-sm font-normal text-gray-500">/{tier.period}</span>}
                    </p>
                    {tier.popular && (
                      <span className={`${badgeClasses[tier.color]} text-white text-xs px-2 py-0.5 rounded-full mt-2 inline-block`}>
                        ⭐ Más Popular
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Expanded Features */}
            {showTiers && (
              <div className="grid md:grid-cols-3 gap-4 pt-4 border-t border-gray-200">
                {SUBSCRIPTION_TIERS.map(tier => (
                  <div key={tier.id} className="text-sm">
                    <h4 className="font-semibold mb-2 text-gray-700">{tier.name}</h4>
                    <ul className="space-y-1">
                      {tier.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-1.5">
                          <span className={tier.id === 'FREE' ? 'text-gray-400' : 'text-primary'}>✓</span>
                          <span className="text-gray-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {/* Selected Plan Summary */}
            <div className="mt-4 p-3 bg-primary/10 rounded-lg">
              <p className="text-sm text-gray-700">
                <strong>Plan seleccionado:</strong> {SUBSCRIPTION_TIERS.find(t => t.id === form.subscription_plan)?.name}
                {form.subscription_plan !== 'FREE' && (
                  <span className="text-primary font-semibold ml-1">
                    (₡{SUBSCRIPTION_TIERS.find(t => t.id === form.subscription_plan)?.price.toLocaleString('es-CR')}/mes)
                  </span>
                )}
              </p>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full btn-primary mt-6 disabled:opacity-50"
          >
            {loading ? 'Creando cuenta...' : 'Crear Cuenta'}
          </button>

          <div className="mt-6 p-4 bg-blue-50 rounded-xl text-sm text-blue-700">
            <strong>💡 Info:</strong> Las cuentas <strong>gratuitas</strong> se activan automáticamente. Los planes <strong>PRO/PREMIUM</strong> requieren aprobación del administrador.
          </div>

          <p className="text-center text-sm text-gray-500 mt-6">
            ¿Ya tienes cuenta?{' '}
            <Link to="/login" className="text-primary font-semibold">
              Inicia sesión
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
